/**
 * Main controlled note field with optional AI structuring.
 *
 * Drop-in replacement for a speech-enabled textarea. Supports three modes:
 * 1. Plain compose — type / dictate freely, then structure via the AI icon next to the mic
 * 2. Split compose — edit original (with STT) and structured sections together
 * 3. Display — show structured note on reopen; "Edit note" returns to split mode
 *
 * Emits a plain string or JSON payload via `onChange`; the parent form owns save.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Flex, Modal, Space, Tooltip, Typography, theme } from 'antd';
import { RobotOutlined } from '@ant-design/icons';
import type { TextAreaProps } from 'antd/es/input';
import { useTranslation } from 'react-i18next';
import { useStructureNoteMutation } from '@/api/agents/mutations';
import { OriginalNoteEditor } from './OriginalNoteEditor';
import { StructuredNoteFieldShell } from './StructureNoteLoadingOverlay';
import { StructuredNoteDisplay } from './StructuredNoteDisplay';
import { StructuredNoteEditor } from './StructuredNoteEditor';
import {
  createAiSnapshot,
  createInitialFieldState,
  isStructuredNoteDirty,
  serializeNoteState,
} from './serialization';
import type { StructuredNoteFieldState, StructuredNoteViewMode } from './types';

const { Text } = Typography;

/** Props mirror a TextArea plus a string `value` / `onChange` for Form.Item or controlled modals. */
export interface StructuredNoteFieldProps extends Pick<TextAreaProps, 'rows' | 'maxLength' | 'showCount' | 'placeholder' | 'disabled'> {
  /** Stored note string (plain text or structured JSON). */
  value?: string;
  /** Called whenever the note should be persisted by the parent form. */
  onChange?: (value: string) => void;
  id?: string;
}

/** Notifies the parent with the serialized form of the current field state. */
function emitChange(
  onChange: StructuredNoteFieldProps['onChange'],
  state: StructuredNoteFieldState,
) {
  onChange?.(serializeNoteState(state));
}

/**
 * AI-assisted note field used in appointment and cvManager note forms.
 *
 * @see StructuredNoteFieldProps
 */
export const StructuredNoteField = React.forwardRef<HTMLTextAreaElement, StructuredNoteFieldProps>(
  function StructuredNoteField(
    { value = '', onChange, rows = 5, maxLength, showCount = true, placeholder, disabled, id },
    ref,
  ) {
    const { token } = theme.useToken();
    const { t } = useTranslation('common');
    const structureNoteMutation = useStructureNoteMutation();
    const [state, setState] = useState<StructuredNoteFieldState>(() => createInitialFieldState(value));
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const lastExternalValueRef = useRef(value);

    useEffect(() => {
      if (value === lastExternalValueRef.current) return;
      lastExternalValueRef.current = value;
      setState(createInitialFieldState(value));
      setErrorMessage(null);
    }, [value]);

    const updateState = useCallback(
      (updater: (prev: StructuredNoteFieldState) => StructuredNoteFieldState) => {
        setState((prev) => {
          const next = updater(prev);
          lastExternalValueRef.current = serializeNoteState(next);
          emitChange(onChange, next);
          return next;
        });
      },
      [onChange],
    );

    const handleOriginalChange = useCallback(
      (nextContent: string) => {
        updateState((prev) => ({
          ...prev,
          originalContent: nextContent,
          viewMode: prev.viewMode === 'display' ? 'compose-split' : prev.viewMode,
        }));
      },
      [updateState],
    );

    const handleStructuredChange = useCallback(
      (structuredNote: StructuredNoteFieldState['structuredNote']) => {
        updateState((prev) => ({
          ...prev,
          structuredNote,
          viewMode: prev.viewMode === 'display' ? 'compose-split' : prev.viewMode,
        }));
      },
      [updateState],
    );

    const applyStructuredResult = useCallback(
      (structuredNote: StructuredNoteFieldState['structuredNote']) => {
        updateState((prev) => ({
          ...prev,
          viewMode: 'compose-split' as StructuredNoteViewMode,
          structuredNote,
          lastAiSnapshot: createAiSnapshot(structuredNote),
        }));
      },
      [updateState],
    );

    const runStructure = useCallback(async () => {
      const trimmed = state.originalContent.trim();
      if (!trimmed) return;

      setErrorMessage(null);
      try {
        const structuredNote = await structureNoteMutation.mutateAsync({
          originalNoteContent: trimmed,
        });
        applyStructuredResult(structuredNote);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : t('structuredNote.errors.generic'));
      }
    }, [applyStructuredResult, state.originalContent, structureNoteMutation, t]);

    const handleStructureClick = useCallback(() => {
      if (state.viewMode === 'compose-split' && isStructuredNoteDirty(state.structuredNote, state.lastAiSnapshot)) {
        Modal.confirm({
          title: t('structuredNote.regenerateConfirm.title'),
          content: t('structuredNote.regenerateConfirm.message'),
          okText: t('structuredNote.regenerateConfirm.continue'),
          cancelText: t('actions.cancel'),
          onOk: () => void runStructure(),
        });
        return;
      }

      void runStructure();
    }, [runStructure, state.lastAiSnapshot, state.structuredNote, state.viewMode, t]);

    const handleEditNote = useCallback(() => {
      updateState((prev) => ({
        ...prev,
        viewMode: 'compose-split',
      }));
    }, [updateState]);

    const isLoading = structureNoteMutation.isPending;
    const canStructure = state.originalContent.trim().length > 0 && !disabled && !isLoading;
    const structureLabel =
      state.viewMode === 'compose-split'
        ? t('structuredNote.updateStructure')
        : t('structuredNote.structureWithAi');

    const structureAction = (
      <Tooltip title={structureLabel}>
        <Button
          type="text"
          size="small"
          aria-label={structureLabel}
          icon={<RobotOutlined />}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleStructureClick();
          }}
          disabled={!canStructure}
          style={{
            color: canStructure ? token.colorTextSecondary : undefined,
          }}
        />
      </Tooltip>
    );

    if (state.viewMode === 'display') {
      return (
        <div id={id} ref={ref as React.Ref<HTMLDivElement>} style={{ width: '100%' }}>
          <StructuredNoteDisplay
            structuredNote={state.structuredNote}
            showEditButton={!disabled}
            onEdit={handleEditNote}
          />
        </div>
      );
    }

    if (state.viewMode === 'compose-split') {
      return (
        <StructuredNoteFieldShell id={id} fieldRef={ref as React.Ref<HTMLDivElement>} isLoading={isLoading}>
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            <Flex
              gap={token.marginMD}
              align="stretch"
              wrap="wrap"
              style={{ width: '100%', alignItems: 'stretch' }}
            >
              <div
                style={{
                  flex: '1 1 280px',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                }}
              >
                <OriginalNoteEditor
                  value={state.originalContent}
                  onChange={handleOriginalChange}
                  rows={rows}
                  maxLength={maxLength}
                  showCount={Boolean(showCount)}
                  placeholder={placeholder}
                  disabled={disabled || isLoading}
                  extraActions={structureAction}
                  fillHeight
                />
              </div>
              <div
                style={{
                  flex: '1 1 280px',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                }}
              >
                <Text strong style={{ display: 'block', marginBottom: token.marginXS, flexShrink: 0 }}>
                  {t('structuredNote.structuredNote')}
                </Text>
                <StructuredNoteEditor
                  value={state.structuredNote}
                  onChange={handleStructuredChange}
                  disabled={disabled || isLoading}
                />
              </div>
            </Flex>
            {errorMessage ? <Alert type="error" showIcon title={errorMessage} /> : null}
          </Space>
        </StructuredNoteFieldShell>
      );
    }

    return (
      <StructuredNoteFieldShell id={id} fieldRef={ref as React.Ref<HTMLDivElement>} isLoading={isLoading}>
        <Space orientation="vertical" size="small" style={{ width: '100%' }}>
          <OriginalNoteEditor
            value={state.originalContent}
            onChange={handleOriginalChange}
            rows={rows}
            maxLength={maxLength}
            showCount={Boolean(showCount)}
            placeholder={placeholder}
            disabled={disabled || isLoading}
            extraActions={structureAction}
            showLabel={false}
          />
          {errorMessage ? <Alert type="error" showIcon title={errorMessage} /> : null}
        </Space>
      </StructuredNoteFieldShell>
    );
  },
);

StructuredNoteField.displayName = 'StructuredNoteField';

export default StructuredNoteField;
